import '@caresend/types';
import { formatTimeZoneDate, procedureDateInfo, isNullish } from '@caresend/utils';
import { formatOpeningHours } from './format.js';
import { usePortalStore } from '../../store/portal/index.js';
import { getStore } from '../../../../../store/helpers/initStore.js';

/** In miles */
const DEVIATION_LIMIT = 1000 / 5280;
const formatWaypointDate = (date) => {
    var _a;
    const currentYear = new Date().getFullYear();
    const draftWaypointYear = new Date((_a = date.timestamp) !== null && _a !== void 0 ? _a : 0).getFullYear();
    let formattedDate = `${formatTimeZoneDate(date, 'MMM Do')}`;
    if (draftWaypointYear && draftWaypointYear !== currentYear) {
        formattedDate += `, ${draftWaypointYear}`;
    }
    formattedDate += ` at ${formatTimeZoneDate(date, 'h:mm a')}`;
    return formattedDate;
};
const getItineraryWaypointDateTimeInfo = (waypoint) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const portalStore = usePortalStore();
    let timeInfo;
    const waypointDate = (_a = portalStore.waypoints.get(waypoint.id)) === null || _a === void 0 ? void 0 : _a.date;
    const dateInfo = (_b = portalStore.waypoints.get(waypoint.id)) === null || _b === void 0 ? void 0 : _b.computed.dateTimeRequest;
    const draftWaypointID = Object.keys((_c = waypoint.draftWaypoints) !== null && _c !== void 0 ? _c : {})[0];
    const draftWaypoint = portalStore.draftWaypoints.getRaw(draftWaypointID !== null && draftWaypointID !== void 0 ? draftWaypointID : '');
    const groups = [];
    if (waypointDate) {
        timeInfo = `Scheduled for ${formatWaypointDate(waypointDate)}`;
    }
    else if (!dateInfo && ((_d = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.finalizedDate) === null || _d === void 0 ? void 0 : _d.date)) {
        timeInfo = `Requested for ${formatWaypointDate((_e = draftWaypoint.finalizedDate) === null || _e === void 0 ? void 0 : _e.date)}`;
    }
    else if (!dateInfo && (draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.dateRange)) {
        const startDate = formatTimeZoneDate((_f = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.dateRange) === null || _f === void 0 ? void 0 : _f.startDate, 'MMMM D');
        const endDate = formatTimeZoneDate((_g = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.dateRange) === null || _g === void 0 ? void 0 : _g.endDate, 'MMMM D');
        timeInfo = `Requested between ${startDate} and ${endDate}`;
    }
    else {
        timeInfo = dateInfo ? procedureDateInfo(dateInfo) : '';
    }
    const requestAndTransit = [{ description: timeInfo }];
    groups.push(requestAndTransit);
    if (waypoint.transitInfo) {
        const { arrivalLocation, arrivalTime, completionLocation, completionTime, delayInfo, departureTime, estimatedTransitDuration, } = waypoint.transitInfo;
        if (departureTime) {
            requestAndTransit.push({
                description: `Transit start ${formatTimeZoneDate(departureTime, 'h:mm a')}`,
            });
        }
        if (estimatedTransitDuration) {
            requestAndTransit.push({
                description: `Transit Est. ${Math.round(estimatedTransitDuration / 60)} min`,
            });
        }
        const transitDelay = [];
        if (delayInfo) {
            const delayTimeZoneDate = {
                timestamp: delayInfo.timestamp,
                timeZone: delayInfo.timeZone,
            };
            const delayString = `Delay reported ${formatTimeZoneDate(delayTimeZoneDate, 'h:mm a')}`;
            const delayedTransitDurationString = isNullish(delayInfo.newDrivingEstimate) ? undefined
                : `New Transit Est. ${Math.round(delayInfo.newDrivingEstimate / 60)} min`;
            transitDelay.push({ description: delayString });
            if (delayedTransitDurationString)
                transitDelay.push({ description: delayedTransitDurationString });
        }
        groups.push(transitDelay);
        const buildDescription = (time, departedTooFar, locationDeviation) => {
            const completedTimeText = formatTimeZoneDate(time, 'h:mm a');
            const departedTooFarText = departedTooFar ? `(off by ${locationDeviation.toFixed(1)} miles)` : '';
            return `${completedTimeText} ${departedTooFarText}`;
        };
        const arrivalDelay = [];
        if (arrivalTime) {
            const locationDeviation = portalStore.waypoints.getters.deviation(waypoint.id, arrivalLocation, 'miles');
            const arrivedTooFar = locationDeviation > DEVIATION_LIMIT;
            const arrivedAfterStartTime = ((_j = (_h = waypoint.date) === null || _h === void 0 ? void 0 : _h.timestamp) !== null && _j !== void 0 ? _j : 0) < ((_k = arrivalTime.timestamp) !== null && _k !== void 0 ? _k : 0);
            const icons = [
                ...(arrivedTooFar ? ['map-marker'] : []),
                ...(arrivedAfterStartTime ? ['clock'] : []),
            ];
            arrivalDelay.push({
                icons,
                ...((arrivedTooFar || arrivedAfterStartTime) && { customStyle: 'danger' }),
                description: `Arrived at ${buildDescription(arrivalTime, arrivedTooFar, locationDeviation)}`,
            });
        }
        if (completionTime) {
            const locationDeviation = portalStore.waypoints.getters.deviation(waypoint.id, completionLocation, 'miles');
            const departedTooFar = locationDeviation > DEVIATION_LIMIT;
            const departedAfterEndTime = (_l = portalStore.waypoints.get(waypoint.id)) === null || _l === void 0 ? void 0 : _l.computed.nurseDepartedLate;
            const icons = [
                ...(departedTooFar ? ['map-marker'] : []),
                ...(departedAfterEndTime ? ['clock'] : []),
            ];
            arrivalDelay.push({
                icons,
                ...((departedTooFar || departedAfterEndTime) && { customStyle: 'danger' }),
                description: `Completed at ${buildDescription(completionTime, departedTooFar, locationDeviation)}`,
            });
        }
        groups.push(arrivalDelay);
    }
    if (waypoint.placeID) {
        const store = getStore();
        const openingHours = (_o = (_m = store.state.places.places) === null || _m === void 0 ? void 0 : _m[waypoint.placeID]) === null || _o === void 0 ? void 0 : _o.openingHours;
        if (openingHours) {
            const formattedOpeningHours = formatOpeningHours(openingHours, 'short').map(({ days, times }) => `${days} (${times})`);
            const group2 = [{ description: `Open ${formattedOpeningHours.join(', ')}` }];
            groups.push(group2);
        }
    }
    return groups;
};

export { getItineraryWaypointDateTimeInfo };
